import React, { useState } from 'react';
import { FileText, X, AlertTriangle, Camera, Loader } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import companyLogo from './MElogo.jpeg'
import frontviewImage from './frontview.png'
import sideviewImage from './Sideview.png'
import topviewImage from './topview.png'
import Chart from "chart.js/auto";
import * as THREE from 'three';
import ReactDOM from "react-dom";
// import { BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const formatMeasurement = (value, unit) => {
  if(value == 0) return '---';
  return value ? `${Number(value).toFixed(2)} ${unit}` : '---';
};

const generateUniqueId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

const ReportModal = ({ 
  isOpen, 
  onClose, 
  measurements, 
  viewer,
  uploadedFileName,
  // URL to company logo
}) => {
  const [formData, setFormData] = useState({
    stopeName: '',
    projectName: '',
    surveyorName: '',
    stopeDetails: true,
    underbreakCalculation: true,
    overbreakCalculation: true,
    volumeCalculation: true,
    qualityMetrics: true,
    notes: '',
    reportNumber: generateUniqueId()
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const dataPercentage = [
    { name: "Overbreak", value: 20 },
    { name: "Underbreak", value: 40 },
  ];
  const COLORS = ["#0088FE", "#FF0000"];
  

  const capturePointCloudView = async (view) => {
    if (!viewer?.renderer?.domElement) return null;
    const canvas = viewer.renderer.domElement;
    // Example Usage
    setView(view); // Set side view

    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for 500ms

    return await html2canvas(canvas);
  };
  const setView = (viewType) => {
    const viewer = window.potreeViewer;
    if (!viewer) {
        console.error("Potree viewer not found.");
        return;
    }

    // Ensure THREE.js is available
    const THREE = window.THREE || window.potreeViewer?.THREE;
    if (!THREE) {
        console.error("THREE.js is not available.");
        return;
    }

    const view = viewer.scene.view;
    const camera = viewer.scene.getActiveCamera();

    if (!view || !camera) {
        console.error("Potree view or camera not found.");
        return;
    }

    // Get the current target (center of rotation)
    const target = view.getPivot(); // Correct target point
    const radius = view.radius || 1000; // Ensure a valid radius

    let newPosition, newYaw, newPitch;

    switch (viewType) {
        case "top":
            newPosition = new THREE.Vector3(target.x, target.y, target.z + radius); // Move camera above target
            newYaw = view.yaw; 
            newPitch = -Math.PI / 2; // Look straight down
            break;
        case "side":
            newPosition = new THREE.Vector3(target.x + radius, target.y, target.z); // Move to side
            newYaw =  Math.PI / 2; // Rotate 90° for a side view
            newPitch = 0; 
            break;
        case "front":
            newPosition = new THREE.Vector3(target.x, target.y, target.z + radius); // Move to front
            newYaw = -(view.yaw + radius/100);  // Face the front
            newPitch = -Math.PI / 2;
            break;
        default:
            console.warn("Unknown view type:", viewType);
            return;
    }

    // Apply new camera settings
    view.position.copy(newPosition);
    view.yaw = newYaw;
    view._pitch = newPitch;

    camera.position.copy(newPosition);
    camera.lookAt(target); // Ensure camera is always looking at the pivot

    // Ensure controls update correctly
    viewer.setControls(viewer.orbitControls);

    // Request redraw to apply changes
    if (viewer.requestRedraw) {
        viewer.requestRedraw();
    } else {
        console.warn("requestRedraw is not available. Falling back to renderer.clear().");
        viewer.renderer.clear();
        viewer.renderer.render(viewer.scene.scene, camera);
    }
};


  const addHeaderSection = (pdf, companyLogo) => {
    // Add company logo
    const img = new Image();
    img.src = companyLogo;
    if (companyLogo) {
      pdf.addImage(companyLogo, 'PNG', 20, 15, 20, 20);
    }

    // Add report title and metadata
    pdf.setFontSize(20);
    pdf.setTextColor(44, 62, 80);
    pdf.text('Stope Analysis Report', pdf.internal.pageSize.getWidth() / 2, 25, { align: 'center' });

    // Add report metadata
    pdf.setFontSize(10);
    pdf.setTextColor(127, 140, 141);
    const metadataY = 40;
    pdf.text(`Report Number: ${formData.reportNumber}`, 20, metadataY);
    pdf.text(`Date: ${formatDate(new Date())}`, 20, metadataY + 6);
    pdf.text(`Project: ${formData.projectName || '---'}`, 20, metadataY + 12);
    pdf.text(`Stope ID: ${formData.stopeName}`, pdf.internal.pageSize.getWidth() - 80, metadataY);
    pdf.text(`Surveyor: ${formData.surveyorName || '---'}`, pdf.internal.pageSize.getWidth() - 80, metadataY + 6);
    //pdf.text(`File: ${uploadedFileName || '---'}`, pdf.internal.pageSize.getWidth() - 80, metadataY + 12);

    return metadataY + 25; // Return next Y position
  };

  const addSectionWithBorder = (pdf, title, yPos, sectionHeight) => {
    const margin = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const sectionWidth = pageWidth - 2 * margin;
  
    // Draw border for the section
    //pdf.setDrawColor(0); // Black color for the border
    //pdf.rect(margin, yPos, sectionWidth, sectionHeight); // Draw rectangle
  
    // Add section header

    pdf.setFillColor(236, 240, 241);
    pdf.rect(margin, yPos, pdf.internal.pageSize.getWidth() - 40, 7, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(44, 62, 80);
    pdf.text(title, margin + 2, yPos + 5);
    return yPos + 12; // Move Y position down for content inside section
  };
  

  const addMeasurementTable = (pdf, yPos) => {
    const startX = 20;
    const colWidth = 50;
    const rowHeight = 7;
    
    // Table headers
    pdf.setFillColor(236, 240, 241);
    pdf.rect(startX, yPos, pdf.internal.pageSize.getWidth() - 40, rowHeight, 'F');
    pdf.setFontSize(10);
    pdf.setTextColor(44, 62, 80);
    pdf.text('Volume Analysis Result', startX + 2, yPos + 5);
    //pdf.text('Design', startX + colWidth + 2, yPos + 5);
    //pdf.text('Actual', startX + (colWidth * 2) + 2, yPos + 5);
    //pdf.text('Variance', startX + (colWidth * 3) + 2, yPos + 5);

    yPos += rowHeight;

    // Volume data
    const addRow = (label, value, unit) => {
      pdf.setTextColor(44, 62, 80);
      pdf.text(label, startX + 2, yPos + 5);
      pdf.text(formatMeasurement(value, unit), startX + (colWidth + 2) + 2, yPos + 5);
      yPos += rowHeight;
    };
    const addRowHorizontal = (label, value, unit) => {
      pdf.setTextColor(44, 62, 80);
      pdf.text(label, startX + (colWidth * 2), yPos-2);
      pdf.text(formatMeasurement(value, unit), startX + (colWidth * 3), yPos-2);
      //yPos += rowHeight;
    };
  
    if (measurements?.volume) {
       addRow('Actual Volume', measurements.volume.totalVolume.toFixed(2), 'm³');
       addRowHorizontal("Planned Volume",measurements.plannedDesignVolume.volume.toFixed(2),'m³');
    }

    if (measurements?.overbreak) {
      addRow('Overbreak', measurements.overbreak.volume, 'm³');
      addRowHorizontal('Overbreak %', measurements.overbreak.percentage, '%');
    }

    if (measurements?.underbreak) {
      addRow('Underbreak', measurements.underbreak.volume,'m³');
      addRowHorizontal('Underbreak %',measurements.underbreak.percentage, '%');
    }
      addRow('Underbreak Max Length', measurements.underbreak.maxLength,'m');
      addRowHorizontal('Overbreak Max Length ',measurements.overbreak.maxLength, 'm');
      addRow('Ore Dillution', measurements.oreDillution,'%');

    return yPos;
  };

  const addLineGraphToPDF = async (pdf, yPos, pageHeight) => {
  return new Promise(async (resolve) => {
    const chartCanvas = document.createElement("canvas");
    chartCanvas.width = pdf.internal.pageSize.getWidth() - 40; 
    chartCanvas.height = 250; 
    document.body.appendChild(chartCanvas);

    const ctx = chartCanvas.getContext("2d");

    // Creating a line chart
    new Chart(ctx, {
      type: "line",
      data: {
        labels: ["Planned Volume", "Actual Volume", "Overbreak", "Underbreak"],
        datasets: [
          {
            label: "Volume (m³)",
            data: [2456.99, 2412.36, 171.52, 216.79],
            borderColor: "blue",
            backgroundColor: "rgba(0, 0, 255, 0.1)", // Light blue fill
            fill: true,
            borderWidth: 2,
            pointBackgroundColor: "red",
            pointRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              font: {
                size: 16, 
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: 16, 
              },
            },
          },
          y: {
            beginAtZero: true,
            max: 3000,
            ticks: {
              font: {
                size: 16,
              },
            },
          },
        },
      },
    });

    // Wait for the chart to render
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Convert the chart to an image
    html2canvas(chartCanvas, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");

      let chartHeight = 70; 
      if (yPos + chartHeight > pageHeight - 20) {
        chartHeight = pageHeight - yPos - 20;
      }

      // Add the line graph to the PDF
      pdf.addImage(imgData, "PNG", 20, yPos, pdf.internal.pageSize.getWidth() - 40, chartHeight);
      document.body.removeChild(chartCanvas);
      resolve(yPos + chartHeight + 10); 
    });
  });
};


const addPieChartToPDF = async (pdf, yPos, pageHeight) => {
  return new Promise(async (resolve) => {
    const chartCanvas = document.createElement("canvas");
    chartCanvas.width = 120; // Reduce width for better clarity
    chartCanvas.height = 180; // Reduce height for better clarity
    document.body.appendChild(chartCanvas);

    const ctx = chartCanvas.getContext("2d");

    // Create a Pie Chart with a smaller radius
    new Chart(ctx, {
      type: "pie",
      data: {
        labels: ["Overbreak", "Underbreak"],
        datasets: [
          {
            data: [20, 40], // Replace with dynamic values if needed
            backgroundColor: ["#0088FE", "#FF0000"],
            hoverBackgroundColor: ["#0056b3", "#cc0000"],
          },
        ],
      },
      options: {
        responsive: false, // Set to false since we are using a fixed size
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom", // Moves legend below for better spacing
            labels: {
              font: {
                size: 10, // Smaller font for better fit
              },
            },
          },
        },
      },
    });

    // Wait for the chart to render
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Convert the chart to an image
    html2canvas(chartCanvas, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");

      let chartHeight = 50; // Reduce height for compact display
      if (yPos + chartHeight > pageHeight - 20) {
        chartHeight = pageHeight - yPos - 20;
      }

      // Add the Pie Chart to the PDF with a smaller size
      pdf.addImage(imgData, "PNG", 70, yPos, 50, chartHeight); // Adjust positioning and size
      document.body.removeChild(chartCanvas);
      resolve(yPos + chartHeight + 10);
    });
  });
};



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const pointCloudSnapshot = await capturePointCloudView("side");
      const pointCloudSnapshot1 = await capturePointCloudView("top"); 
      const pointCloudSnapshot2 = await capturePointCloudView("front"); 
    
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = pdf.internal.pageSize.getHeight(); // Get available page height
      let yPos = addHeaderSection(pdf, companyLogo);

      yPos = addMeasurementTable(pdf, yPos-10);
      let sectionHeight = 100; // Adjust height based on content
      yPos = addSectionWithBorder(pdf, 'Stope Profile Side View', yPos + 5, sectionHeight);
      // **1️⃣ Add Point Cloud Snapshot First**
      if (pointCloudSnapshot) {
        const imgWidth = pdf.internal.pageSize.getWidth() - 40;
        const imgHeight = (pointCloudSnapshot.height * imgWidth) / pointCloudSnapshot.width;
        pdf.addImage(pointCloudSnapshot.toDataURL('image/jpeg', 0.8), 'JPEG', 20, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 5;
      }
     //pdf.addImage(sideviewImage, 'JPEG', 60, yPos-4, 100, 80);
      //yPos += 80 + 10;

      yPos = addSectionWithBorder(pdf, 'Stope Profile Top View', yPos + 5, sectionHeight);
      // **1️⃣ Add Point Cloud Snapshot First**
      
      if (pointCloudSnapshot1) {
        const imgWidth = pdf.internal.pageSize.getWidth() - 40;
        const imgHeight = (pointCloudSnapshot1.height * imgWidth) / pointCloudSnapshot1.width;
        pdf.addImage(pointCloudSnapshot1.toDataURL('image/jpeg', 0.8), 'JPEG', 20, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 5;
      }

      // **2️⃣ Add Measurement Table Below Point Cloud**
      //pdf.setDrawColor(189, 195, 199);
      //pdf.line(20, yPos - 5, pdf.internal.pageSize.getWidth() - 20, yPos - 5);
      //yPos = addMeasurementTable(pdf, yPos-5);

      // **3️⃣ Add Chart Below Measurements**
      pdf.addPage();
      yPos = 5;
      yPos = addSectionWithBorder(pdf, 'Stope Profile Front View', yPos + 5, sectionHeight);
      // **1️⃣ Add Point Cloud Snapshot First**
      
      if (pointCloudSnapshot2) {
        const imgWidth = pdf.internal.pageSize.getWidth() - 40;
        const imgHeight = (pointCloudSnapshot2.height * imgWidth) / pointCloudSnapshot2.width;
        pdf.addImage(pointCloudSnapshot2.toDataURL('image/jpeg', 0.8), 'JPEG', 20, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 5;
      }
      yPos = addSectionWithBorder(pdf, 'Graphical Volume Analysis Result', yPos+2, sectionHeight);
      yPos = await addLineGraphToPDF(pdf, yPos, pageHeight);

      yPos = addSectionWithBorder(pdf, 'Overbreak vs. Underbreak (Percentage)', yPos+2, sectionHeight);
      let overUnderData = [
        { name: "Overbreak", value: measurements.overbreak.percentage },
        { name: "Underbreak", value: measurements.underbreak.percentage },
      ]
      yPos = await addPieChartToPDF(pdf, yPos, pageHeight, dataPercentage, "Overbreak vs. Underbreak (Percentage)");


      // **4️⃣ Add Notes (if any)**
      if (formData.notes) {
        yPos += 10;
        pdf.setFontSize(12);
        pdf.setTextColor(44, 62, 80);
        pdf.text('Notes:', 20, yPos);
        pdf.setFontSize(10);
        pdf.text(formData.notes, 20, yPos + 7);
        yPos += 15; // Move down
      }

      // **Ensure footer is within bounds**
      if (yPos + 10 > pageHeight - 20) {
        yPos = pageHeight - 20;
      }

      // **5️⃣ Add Footer**
      pdf.setFontSize(8);
      pdf.setTextColor(127, 140, 141);
      pdf.text(
        `Generated: ${new Date().toLocaleString()}`,
        pdf.internal.pageSize.getWidth() / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      // Save the PDF
      /*const fileName = `${uploadedFileName?.split('.')[0] || 'stope'}-${formatDate(new Date())}-${formData.reportNumber}.pdf`;
      pdf.save(fileName);*/
      const previewWindow = window.open();
      previewWindow.document.write("<html><head><title>Report Preview</title></head><body><h1>Report Preview</h1></body></html>");
      
      const pdfOutput = pdf.output('bloburl');
      previewWindow.document.body.innerHTML += `<iframe src="${pdfOutput}" width="100%" height="100%" style="border:none;"></iframe>`;

      onClose();

    } catch (err) {
      console.error('Report generation error:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl">
          <div className="relative p-6">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Generate Analysis Report</h3>
              <p className="mt-1 text-sm text-gray-500">
                Report ID: {formData.reportNumber}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={formData.projectName}
                    onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Enter project name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stope ID
                  </label>
                  <input
                    type="text"
                    value={formData.stopeName}
                    onChange={(e) => setFormData({...formData, stopeName: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    required
                    placeholder="Enter stope ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Surveyor Name
                  </label>
                  <input
                    type="text"
                    value={formData.surveyorName}
                    onChange={(e) => setFormData({...formData, surveyorName: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Enter surveyor name"
                  />
                </div>
              </div>

              {/* Current Measurements Preview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Measurement Preview</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <h5 className="text-xs font-medium text-gray-500">Volume Analysis</h5>
                    <div className="text-sm">
                      <div>Ore Volume: {measurements.plannedDesignVolume.volume} m³</div>
                      <div>Actual blast Volume: {measurements.volume.totalVolume.toFixed(2)} m³</div>
                      {/* <div>Surface Area: {formatMeasurement(measurements?.volume?.surfaceArea, 'm²')}</div> */}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h5 className="text-xs font-medium text-gray-500">Overbreak</h5>
                    <div className="text-sm">
                      <div>Volume: {measurements.overbreak.volume} m³</div>
                      <div>Percentage: {measurements.overbreak.percentage} %</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h5 className="text-xs font-medium text-gray-500">Underbreak</h5>
                    <div className="text-sm">
                      <div>Volume: {measurements.underbreak.volume} m³</div>
                      <div>Percentage: {measurements.underbreak.percentage} %</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Report Options */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="volumeCalc"
                    checked={formData.volumeCalculation}
                    onChange={(e) => setFormData({...formData, volumeCalculation: e.target.checked})}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <label htmlFor="volumeCalc" className="ml-3 text-sm">
                    <div className="font-medium text-gray-700">Include Volume Analysis</div>
                    <p className="text-gray-500">Volume and surface area measurements</p>
                  </label>
                </div>

                {/*<div className="flex items-start">
                <input
  type="checkbox"
  id="qualityMetrics"
  checked={formData.qualityMetrics}
  onChange={(e) => setFormData({...formData, qualityMetrics: e.target.checked})}
  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
/>
                  <label htmlFor="qualityMetrics" className="ml-3 text-sm">
                    <div className="font-medium text-gray-700">Include Quality Metrics</div>
                    <p className="text-gray-500">Point density and coverage analysis</p>
                  </label>
                </div>*/}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Add any additional notes, observations, or comments..."
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`flex items-center px-4 py-2 text-sm font-medium text-white rounded-md
                    ${isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Panel */}
          
          
        </div>
      </div>
    </div>
  );
};

export default ReportModal;